import {
  formatCount,
  formatMoney,
  computePctChange,
} from '@/utils/numberUtils';
import useI18n from '@/hooks/useI18n';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ChartSeries {
  name: string;
  /** Nullable so the backend can signal "future bucket" with `null` (line
   *  stops here) vs `0` (real "no activity this period" → flat baseline). */
  data: (number | null)[];
}

interface CustomChartProps {
  chartSeries: ChartSeries[];
  categories: string[] | number[] | Date[];
  TooltipData?: string[];
  maXValue: number;
  hideGrid?: boolean;
  hideYAxisLines?: boolean;
  height?: number;
  rotateLabels?: boolean;
  dualYAxis?: boolean;
  yAxisFormatter?: (value: number) => string;
  /** Override series colors. Length should match chartSeries.length. */
  colors?: string[];
  /** Creator-insights look: adds %-change tooltip rows, hover markers, a themed
   *  crosshair and a lighter gradient. Off by default so other charts that use
   *  this component are visually unchanged. */
  variant?: 'default' | 'creatorInsights';
  /** Word used in the tooltip delta line, e.g. "+24% from previous day". */
  changeComparisonLabel?: string;
  /** Index into `categories` that should be highlighted as "today". */
  highlightCategoryIndex?: number;
}

const CustomChart: React.FC<CustomChartProps> = ({
  chartSeries,
  categories,
  TooltipData = ['EMV €', 'Spending €'],
  maXValue,
  hideGrid = false,
  hideYAxisLines = false,
  height = 350,
  rotateLabels = false,
  dualYAxis = false,
  yAxisFormatter = (value: number) => formatMoney(value),
  colors,
  variant = 'default',
  changeComparisonLabel = 'period',
  highlightCategoryIndex,
}) => {
  const isCreatorInsights = variant === 'creatorInsights';
  const { t } = useI18n();
  const shouldRotate = rotateLabels || (categories as unknown[]).length > 7;
  // "Has data" = any series has at least one non-null point. Zero is real
  // data (a flat bar at baseline) — only `null` (future bucket) signals
  // "nothing to plot here". The old `value > 0` check rejected legitimate
  // all-zero windows and showed the "No Data" overlay incorrectly.
  const hasData = chartSeries.some(
    (series) =>
      Array.isArray(series.data) &&
      series.data.some((value) => value !== null && value !== undefined),
  );

  const primaryData =
    dualYAxis && chartSeries.length === 2
      ? (chartSeries[0]?.data?.filter(
          (v): v is number => typeof v === 'number' && Number.isFinite(v),
        ) ?? [])
      : [];
  const secondaryData =
    dualYAxis && chartSeries.length === 2
      ? (chartSeries[1]?.data?.filter(
          (v): v is number => typeof v === 'number' && Number.isFinite(v),
        ) ?? [])
      : [];
  const primaryAxisMax =
    primaryData.length > 0
      ? Math.ceil(Math.max(...primaryData) * 1.1)
      : undefined;
  // Cap secondary axis so its peak sits at ~60% of chart height, keeping it visually below views
  const secondaryAxisMax =
    secondaryData.length > 0
      ? Math.ceil(Math.max(...secondaryData) / 0.6)
      : undefined;

  const chartOptions: ApexOptions = {
    chart: {
      type: 'area' as const,
      height: 350,
      offsetX: -20,
      // ApexCharts adds 15px of empty space above the plot by default. For the
      // creatorInsights card that stacks on top of the 32px wrapper gap and
      // reads as too large a gap below "Total Views"; zero it so the card's
      // 32px is the true gap (Figma). Other charts keep the 15px default.
      parentHeightOffset: isCreatorInsights ? 0 : 15,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    // Hover markers ONLY for the creatorInsights variant (View = green ring /
    // white center, Like = solid pink dot). Non-variant consumers get no
    // markers config at all, so ApexCharts' default hover dot is preserved
    // exactly (gating just hover.size would still suppress their default dot).
    ...(isCreatorInsights
      ? {
          markers: {
            size: 0,
            strokeWidth: 2,
            colors: ['#fff', '#F3C6E2'],
            strokeColors: ['#0FAF62', '#F3C6E2'],
            hover: { size: 6 },
          },
        }
      : {}),
    stroke: {
      curve: 'straight',
      width:
        chartSeries.length === 3
          ? [2, 2, 2]
          : chartSeries.length === 2
            ? [2, 2]
            : 2,
      // Series 0 (primary) is solid; subsequent series are dashed.
      dashArray:
        chartSeries.length === 3
          ? [0, 5, 5]
          : chartSeries.length === 2
            ? [0, 5]
            : 0,
    },
    fill: {
      type:
        chartSeries.length === 3
          ? ['gradient', 'solid', 'solid']
          : chartSeries.length === 2
            ? ['gradient', 'solid']
            : 'gradient',
      gradient: {
        shadeIntensity: isCreatorInsights ? 0 : 1,
        opacityFrom: isCreatorInsights ? 0.2 : 0.7,
        opacityTo: isCreatorInsights ? 0 : 0.3,
        stops: isCreatorInsights ? [0, 100] : [0, 90, 100],
        // creatorInsights reproduces the Figma fill verbatim:
        // linear-gradient(180deg, rgba(0,174,62,0.2) 17.6%, rgba(0,174,62,0) 100%).
        // Explicit colorStops hold 0.2 across the top band — where the
        // high-sitting View line lives, so the wash stays visible — then fade to
        // transparent. ApexCharts' drawGradient honors colorStops verbatim and
        // ignores the opacity*/stops/shade ramp above for this series; the EMV
        // variant (no colorStops) keeps the 0.7→0.3 ramp.
        ...(isCreatorInsights
          ? {
              colorStops: [
                [
                  { offset: 0, color: '#0FAF62', opacity: 0.2 },
                  { offset: 17.6, color: '#0FAF62', opacity: 0.2 },
                  { offset: 100, color: '#0FAF62', opacity: 0 },
                ],
              ],
            }
          : {}),
      },
      // Only the primary series fills under the curve; comparison series are line-only.
      opacity:
        chartSeries.length === 3
          ? [1, 0, 0]
          : chartSeries.length === 2
            ? [1, 0]
            : 1,
    },
    colors:
      colors ??
      (chartSeries.length === 1 ? ['#0FAF62'] : ['#0FAF62', '#E899CC']),
    xaxis: {
      categories: categories,
      tickAmount:
        (categories as unknown[]).length > 1
          ? (categories as unknown[]).length - 1
          : undefined,
      labels: {
        style: {
          colors: '#383838',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '13px',
          fontWeight: 500,
          cssClass: 'lf-axis-label',
        },
        show: true,
        rotate: shouldRotate ? -45 : 0,
        rotateAlways: shouldRotate,
        trim: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: true,
        stroke: {
          color: isCreatorInsights ? '#113E53' : '#191B1C',
          width: 1,
          dashArray: 4,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    // Single yaxis key — a JS object literal silently keeps only the last
    // duplicate key, so an earlier `yaxis: { ... }` here would be silently
    // discarded by the dual-axis ternary below. Branch on `dualYAxis` once.
    yaxis:
      dualYAxis && hasData && chartSeries.length === 2
        ? [
            {
              title: {
                text: 'Views',
                style: { color: '#0FAF62', fontSize: '11px', fontWeight: 500 },
              },
              labels: {
                formatter: yAxisFormatter,
                style: { colors: '#0FAF62' },
                show: true,
              },
              min: 0,
              max: primaryAxisMax,
              tickAmount: 4,
              axisBorder: { show: !hideYAxisLines },
              axisTicks: { show: !hideYAxisLines },
            },
            {
              opposite: true,
              title: {
                text: 'Likes',
                style: { color: '#E899CC', fontSize: '11px', fontWeight: 500 },
              },
              labels: {
                formatter: yAxisFormatter,
                style: { colors: '#E899CC' },
                show: true,
              },
              min: 0,
              max: secondaryAxisMax,
              tickAmount: 4,
              axisBorder: { show: false },
              axisTicks: { show: false },
            },
          ]
        : {
            title: {
              style: { color: '#9B919D', fontSize: '12px' },
              text: '',
            },
            labels: {
              formatter: yAxisFormatter,
              style: {
                colors: '#9B919D',
                fontFamily: 'Manrope, sans-serif',
                fontSize: isCreatorInsights ? '12px' : '13px',
                fontWeight: 500,
                cssClass: 'lf-axis-label',
              },
              show: true,
            },
            min: 0,
            max: maXValue,
            tickAmount: 5,
            forceNiceScale: true,
            // Figma's y-axis has no rule or tick marks — just labels + the
            // dashed gridline. Hide the border/ticks for the creatorInsights
            // variant only. Values stay data-driven (NOT rounded). Other charts
            // keep their axis line + ticks.
            axisBorder: { show: isCreatorInsights ? false : !hideYAxisLines },
            axisTicks: { show: isCreatorInsights ? false : !hideYAxisLines },
            crosshairs: { show: !hideYAxisLines },
          },
    grid: {
      show: !hideGrid,
      padding: {
        left: 10,
        right: 10,
        top: 0,
        // creatorInsights sits in a fixed-height card; the default 16px below
        // the x-axis labels reads as an empty gap before the card's bottom
        // padding. Zero it for this variant only (EMV chart keeps 16px).
        bottom: isCreatorInsights ? 0 : 16,
      },
      xaxis: {
        lines: {
          show: !hideGrid,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      borderColor: hideGrid
        ? 'transparent'
        : isCreatorInsights
          ? '#EDEEEF'
          : '#E5E9EB',
      strokeDashArray: hideGrid ? 0 : 4,
    },
    legend: {
      show: false,
    },
    annotations: {
      xaxis:
        highlightCategoryIndex !== undefined &&
        highlightCategoryIndex >= 0 &&
        highlightCategoryIndex < categories.length
          ? [
              {
                x: categories[highlightCategoryIndex] as string | number,
                borderWidth: 1,
                strokeDashArray: 4,
                borderColor: '#9B919D',
              },
            ]
          : [],
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: { show: false },
      style: {
        fontSize: '12px',
      },
      marker: {
        show: false,
      },
      custom: function ({
        series,
        dataPointIndex,
      }: {
        // Mirrors the chart's data contract: null means "future bucket / no
        // data" so the tooltip can render "—" instead of "€0".
        series: (number | null)[][];
        dataPointIndex: number;
      }) {
        const rowStyle = isCreatorInsights
          ? 'color: #020D3A; font-family: Manrope, sans-serif; font-size: 12px; line-height: 16px; text-align: left;'
          : 'color: #292828; font-family: Manrope, sans-serif; font-size: 14px; line-height: 24px; text-align: left;';
        const cardStyle = isCreatorInsights
          ? 'background: #fff; border-radius: 8px; padding: 11px 18px; box-shadow: 0px 2px 6px 2px rgba(0,0,0,0.15); font-family: Manrope, sans-serif; min-width: 174px;'
          : 'background: #fff; border-radius: 12px; padding: 12px 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.08); font-family: Manrope, sans-serif; min-width: 140px;';

        // Render one row per series with the label from TooltipData and the
        // formatted value in bold. `null` is rendered as `—` (future bucket
        // or no-data marker) rather than `€0`, which would otherwise mask
        // genuine missing data.
        const rows = series
          .map((s, i) => {
            const raw = s[dataPointIndex];
            const rawLabel = (TooltipData[i] ?? '').trim();
            const labelOnly = rawLabel.replace(/€\s*$/, '').trim();
            const currency = rawLabel.endsWith('€') ? '€' : '';
            const valueHtml =
              raw === null || raw === undefined
                ? '<strong style="font-weight: 700;">—</strong>'
                : `<strong style="font-weight: 700;">${currency ? formatMoney(raw) : formatCount(raw)}</strong>`;
            const valueRow = `<div style="${rowStyle}">${labelOnly} ${valueHtml}</div>`;
            if (!isCreatorInsights) return valueRow;
            // Second line: % change vs the previous bucket, colored
            // green (up/flat) / red (down). Omitted when there's no baseline.
            const change = computePctChange(raw, s[dataPointIndex - 1]);
            const changeRow = change
              ? `<div style="color: ${
                  change.positive ? '#00AE3E' : '#E5484D'
                }; font-family: Inter, sans-serif; font-weight: 500; font-size: 12px; line-height: 16px;">${
                  change.text
                } from previous ${changeComparisonLabel}</div>`
              : '';
            // 4px gap between series blocks (Figma modal gap).
            return `<div style="${i > 0 ? 'margin-top: 4px;' : ''}">${valueRow}${changeRow}</div>`;
          })
          .join('');

        return `<div style="${cardStyle}">${rows}</div>`;
      },
    },
    noData: {
      text: String(t('common.noData')),
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#9B919D',
        fontSize: '20px',
        fontFamily: 'sans-serif',
      },
    },
  };

  return (
    <div className="[&_.apexcharts-tooltip]:![transform:translateX(-25px)]">
      <Chart
        options={chartOptions}
        series={hasData ? chartSeries : []}
        type="area"
        height={height}
      />
    </div>
  );
};

export default CustomChart;
